import { Injectable } from '@angular/core';
import { AuthService } from 'src/app/core/services/auth.service';
import { environment } from 'src/environments/environment';

export type TrendAgentEvent =
  | { type: 'state'; status: string; label: string }
  | { type: 'text_delta'; text: string }
  | { type: 'ui_block_create'; block: unknown }
  | { type: 'ui_block_patch'; id: string; patch: unknown }
  | { type: 'ui_block_final'; id: string }
  | { type: 'done'; conversationId: string }
  | { type: 'error'; message: string };

export interface TrendAgentStreamRequest {
  conversationId?: string;
  message: string;
  mode: 'trend_analysis';
  context?: Record<string, unknown>;
}

@Injectable({ providedIn: 'root' })
export class TrendAgentService {
  private readonly endpoint = `${environment.trendAgentApiUrl}/v1/trend-analysis/stream`;

  constructor(private authService: AuthService) {}

  public async *streamTrendAnalysis(
    request: TrendAgentStreamRequest
  ): AsyncGenerator<TrendAgentEvent, void, unknown> {
    const token = this.authService.getAccessToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(this.endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok || !response.body) {
      throw new Error(`Trend Agent stream error: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let dataBlock: string[] = [];

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data:')) {
          dataBlock.push(line.slice(5).trimStart());
          continue;
        }

        if (line.trim() === '' && dataBlock.length) {
          const event = this.parseEvent(dataBlock.join('\n'));
          if (event) {
            yield event;
          }
          dataBlock = [];
        }
      }
    }

    if (dataBlock.length) {
      const event = this.parseEvent(dataBlock.join('\n'));
      if (event) {
        yield event;
      }
    }
  }

  private parseEvent(raw: string): TrendAgentEvent | undefined {
    try {
      return JSON.parse(raw) as TrendAgentEvent;
    } catch (error) {
      console.warn('Failed to parse Trend Agent SSE event:', error, raw);
      return undefined;
    }
  }
}
